/* ============================================================
   Trasse Families — the web console's palette.

   Since 2026-10-28 it is the trasseapp.com design (Gabriel: Families
   follows the new design everywhere — the phone moved to the parent
   app's teal the same day): the site's paper, ink and teal, Barlow
   type. It used to be violet so the household console could never be
   mistaken for the blue fleet one; the fleet console is not blue any
   more either, and one family of screens reads as one product.

   Brand rule that governs every string in these files: the child is
   the reason, the VEHICLE is what is tracked. Copy says "see the car
   on the school run" — never "watch your child".
   ============================================================ */

import type { CSSProperties } from 'react';

export const FAM = {
  /** Primary action / focus — the site's teal ink, white text on it. */
  accent: '#00707F',
  accentLight: '#0FB8CE',
  accentPale: '#7FDCEA',
  /** Surface tints for selected cards and chips. */
  tint: '#E2F5F8',
  tint2: '#CDEFF4',
  /** Text. */
  ink: '#0F1419',
  ink2: '#2A3138',
  ink3: '#5D646B',
  muted: '#8C9196',
  /** Structure. */
  line: '#E4E3DE',
  bg: '#F6F5F1',
  card: '#ffffff',
  /** Positive (done, tracked, saved). */
  pos: '#1E7A4C',
  pos2: '#1E7A4C',
  posTint: '#E4F1E9',
  /** Caution (coming soon, pilot ending). */
  amber: '#9A5B00',
  amberTint: '#FBEFD9',
  /** Error. */
  danger: '#B42318',
  dangerTint: '#FDECEA',
  /** The dark "what the driver sees" panel. */
  nightTop: '#16232B',
  nightBottom: '#0E1A22',
  nightInk: '#C9D6DD',
  nightLabel: '#7FDCEA',
} as const;

/** Flat, as the site's buttons are — the old violet gradient is gone. */
export const FAM_GRADIENT = `linear-gradient(135deg, ${FAM.accent}, ${FAM.accent})`;

/** Display face for headings, UI face for everything else. Loaded per-surface via
 *  an @import at the top of the component's own <style> block — index.html ships
 *  other fonts for the fleet console and Families must not change that for
 *  everyone else. */
export const FAMILY_FONT_CSS =
  "@import url('https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700;800&family=Barlow+Semi+Condensed:wght@500;600;700&display=swap');";

export const UI_FONT = "'Barlow', system-ui, -apple-system, 'Segoe UI', sans-serif";
export const DISPLAY_FONT = "'Barlow Semi Condensed', 'Barlow', sans-serif";

/* ── The one price. Never show the superseded $15 / $25 tiers. ───────────── */
/** Flat monthly household price, in KES. Mirrors HOUSEHOLD_PRICE.basic. */
export const FAMILY_PRICE_KES = 1200;
export const FAMILY_PRICE_LABEL = 'KES 1,200/month';
/** The plan line used on the signup checklist and the review step. */
export const FAMILY_PLAN_LINE = 'KES 1,200/month after 7 free days · no card needed to start';
/** Same toggle as HOUSEHOLD_ANALYTICS_KES — analytics plus unlimited children. */
export const FAMILY_ADDON_KES = 300;
export const FAMILY_ADDON_LABEL = 'KES 300/month';
/** Signup wizard, under "Add another child". Extra kids are the add-on. */
export const FAMILY_CHILD_CAP_HINT =
  `Two children on the base plan. Extra children need the ${FAMILY_ADDON_LABEL} add-on.`;
export const FAMILY_PILOT_DAYS = 7;

/** Shared console page wrap — centred so the right strip is not empty. */
export const FAMILY_PAGE_WRAP: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  maxWidth: 1080,
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
};

/** The privacy promise, kept in one place so signup and the console never drift. */
export const TRACKING_ON = [
  'During an active school run — the morning and afternoon trip in your hours.',
  'During authorized duty — a trip you or the driver started in the app.',
];
export const TRACKING_OFF = [
  "The driver's personal time — evenings, weekends, anything outside a run.",
  'Errands and private journeys that are not a school run.',
  'Off-duty movement of the vehicle.',
  'Private location history — we keep the runs, not a life log.',
];
